import React, { useEffect, useState } from 'react';
import { useData } from '../../data/DataProvider';
import { useToast } from '../../shared/components/ToastProvider';
import SlideOver from '../../shared/components/SlideOver';
import AddressAutocomplete from '../../shared/components/AddressAutocomplete';

const EMPTY = {
  name: '',
  client_id: '',
  address_line1: '',
  address_line2: '',
  city: '',
  postcode: '',
  country: '',
  lat: null,
  lng: null,
  notes: '',
  status: 'active',
};

// Property create/edit drawer (PLAN_admin_portal_phase1.md §4 C5.1) — shown
// from a client's Properties tab. Address autocomplete populates the
// structured fields + coordinates; jobs can then be linked to the property
// to prefill their site address.
export default function PropertyForm({ open, onClose, property, onSaved }) {
  const { add, update } = useData();
  const { notify } = useToast();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  // Reset-on-open: re-initialise from the edited property (or empty for new).
  useEffect(() => {
    if (!open) return;
    setForm(property && property.id
      ? {
          name: property.name || '',
          client_id: property.client_id || '',
          address_line1: property.address_line1 || '',
          address_line2: property.address_line2 || '',
          city: property.city || '',
          postcode: property.postcode || '',
          country: property.country || '',
          lat: property.lat ?? null,
          lng: property.lng ?? null,
          notes: property.notes || '',
          status: property.status || 'active',
        }
      : { ...EMPTY, client_id: property?.client_id || '' });
  }, [open, property]);

  if (!form) return null;

  const change = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const addressLine = [form.address_line1, form.address_line2, form.city, form.postcode].filter(Boolean).join(', ');

  const applyPlace = (place) => setForm((current) => ({
    ...current,
    address_line1: place.line1 || current.address_line1,
    address_line2: place.line2 || '',
    city: place.city || '',
    postcode: place.postcode || '',
    country: place.country || '',
    lat: place.lat ?? current.lat,
    lng: place.lng ?? current.lng,
  }));

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      if (property && property.id) {
        await update('properties', property.id, form);
      } else {
        await add('properties', form);
      }
      onClose();
      notify(property && property.id ? 'Property updated' : 'Property added');
      onSaved?.();
    } catch (err) {
      notify(err.message || 'Failed to save property', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SlideOver open={open} title={property && property.id ? 'Edit property' : 'Add property'} onClose={onClose}>
      <form className="drawer-form" onSubmit={save}>
        <label>Name<input value={form.name} onChange={(event) => change('name', event.target.value)} placeholder="e.g. Home, Holiday let, Office" /></label>

        <div className="form-section">
          <div className="form-section-head"><strong>Address</strong></div>
          <AddressAutocomplete onSelect={applyPlace} initial={addressLine} placeholder="Search the property address…" />
          <label>Line 1<input value={form.address_line1} onChange={(event) => change('address_line1', event.target.value)} /></label>
          <label>Line 2<input value={form.address_line2} onChange={(event) => change('address_line2', event.target.value)} /></label>
          <div className="form-row">
            <label>Town / city<input value={form.city} onChange={(event) => change('city', event.target.value)} /></label>
            <label>Postcode<input value={form.postcode} onChange={(event) => change('postcode', event.target.value)} /></label>
          </div>
          <label>Country<input value={form.country} onChange={(event) => change('country', event.target.value)} /></label>
        </div>

        <label>Notes<textarea rows="3" value={form.notes} onChange={(event) => change('notes', event.target.value)} placeholder="Boiler make/model, access notes, alarms…" /></label>

        <label>Status
          <select value={form.status} onChange={(event) => change('status', event.target.value)}>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </select>
        </label>

        <div className="drawer-actions">
          <button type="button" className="secondary-button" onClick={onClose}>Cancel</button>
          <button className="primary-button" disabled={busy}>{busy ? 'Saving…' : 'Save property'}</button>
        </div>
      </form>
    </SlideOver>
  );
}
